<template>
  <div class="upload-container">
    <!-- 图片列表，支持拖拽排序 -->
    <div class="image-list">
      <div
        v-for="(file, index) in fileList"
        :key="file.uid"
        class="image-item"
        :class="{ 'is-dragging': draggingIndex === index, 'is-drag-over': dragOverIndex === index && draggingIndex !== index }"
        draggable="true"
        @dragstart="handleDragStart(index, $event)"
        @dragover.prevent="handleDragOver(index, $event)"
        @dragenter.prevent="handleDragOver(index, $event)"
        @drop.prevent="handleDrop(index, $event)"
        @dragend="handleDragEnd"
      >
        <img :src="file.thumbUrl || file.url" @click="handlePreview(file)" draggable="false" />
        <div class="drag-tip"><a-icon type="drag" /></div>
        <div class="delete-btn" @click.stop="removeFile(file)">
          <a-icon type="delete" />
        </div>
        <div class="image-controls">
          <a-button
            class="arrow-btn"
            type="link"
            :disabled="index === 0"
            @click.stop="moveImage(index, 'left')">
            <a-icon type="left" />
          </a-button>
          <a-button
            class="arrow-btn"
            type="link"
            :disabled="index === fileList.length - 1"
            @click.stop="moveImage(index, 'right')">
            <a-icon type="right" />
          </a-button>
        </div>
      </div>
    </div>
    <a-upload
      v-model="fileList"
      listType="picture-card"
      :fileList="fileList"
      :remove="removeFile"
      :multiple="true"
      :show-upload-list="false"
      :before-upload="beforeUpload"
      :customRequest="customRequest"
      @preview="handlePreview">
      <div v-if="maxCount === -1 || maxCount > fileList.length">
        <a-icon :type="loading ? 'loading' : 'plus'" />
        <div v-show="!loading" class="ant-upload-text">上传</div>
      </div>
    </a-upload>

    <!-- 预览弹窗 -->
    <a-modal :visible="previewVisible" :footer="null" @cancel="previewCancel">
      <img v-if="type === 'img'" alt="example" style="width: 100%" :src="previewImage" />
      <video
        v-else-if="type === 'video'"
        ref="previewVideo"
        style="width: 100%"
        :src="previewImage"
        :poster="previewImage + '?x-oss-process=video/snapshot,t_0,f_jpg'"
        :controls="true">
        <source :src="previewImage" type="video/mp4" />
      </video>
      <div v-else>
        <div>
          文件暂不支持在线预览, 请点击下载至本地后, 进行查看 <br /> <a :href="previewImage"  target="_blank">下载文件</a>
        </div>
      </div>
    </a-modal>
  </div>
</template>
<script>
import { generateFilePath, uploadObject } from '@/api/tool/oss'

export default {
  props: {
    value: [String, Array],
    type: String, // 文件类型, "img" 图片, "video" 视频, null 不限制类型
    // 最大文件大小, 单位 兆M, 默认 20兆
    maxSize: {
      type: Number,
      default: 20
    },
    /* 文件夹前缀 */
    prefix: {
      type: String,
      default: 'upload'
    },
    // 最大数量, 1 限制1张
    maxCount: {
      type: Number,
      default: 1
    },
    isArrayData: {
      type: Boolean,
      default: false
    }
  },
  created() {
    this.valueChange(this.value)
  },
  data() {
    return {
      fileList: [], // 文件集合
      loading: false, // 加载状态
      previewVisible: false, // 预览弹窗隐藏/显示
      previewImage: '', // 预览 img url
      draggingIndex: null, // 正在拖拽的下标
      dragOverIndex: null, // 拖拽悬停的下标
      uidSeed: 0 // uid 自增种子
    }
  },
  methods: {
    // 删除图片
    removeFile(file) {
      this.fileList = this.fileList.filter(item => item.url !== file.url)
      this.emitChange()
    },
    // 预览按钮点击
    handlePreview(file) {
      this.previewImage = file.url
      this.previewVisible = true
    },
    // 上传之前校验
    beforeUpload(file) {
      // 图片校验
      if (this.type === 'img' && !file.type.startsWith('image')) {
        this.$message.error('上传请上传图片文件')
        return false
      }
      // 视频校验
      if (this.type === 'video' && !file.type.startsWith('video/mp4')) {
        this.$message.error('上传请上传 mp4 视频文件')
        return false
      }
      // 大小校验
      if (file.size / 1024 / 1024 > this.maxSize) {
        this.$message.error(`上传文件大小不能超过 ${this.maxSize} MB`)
        return false
      }
    },
    // 自定义上传请求
    customRequest(fileInfo, index) {
      this.loading = true

      // 文件
      const uploadFile = fileInfo.file

      // 文件夹目录
      const prefix = this.prefix
      const url = this.buildOssUrl(uploadFile.name, prefix)

      // oss 上传
      uploadObject(url, uploadFile).then(res => {
        this.fileList.push(this.createAntFileObj(res))
        this.$message.success('上传成功')
        this.loading = false
        this.emitChange()
      })
    },
    // 构建 oss url
    buildOssUrl(fileName, prefix) {
      // 获取文件后缀
      const suffix = fileName.substring(fileName.lastIndexOf('.'))
      // 生成文件名称
      const filePath = generateFilePath(prefix, suffix)
      return filePath
    },
    // 文件 uid（自增，保证唯一，避免拖拽排序时 key 重复）
    getUidRandom() {
      this.uidSeed += 1
      return `oss_${Date.now()}_${this.uidSeed}`
    },
    // 预览关闭
    previewCancel() {
      this.previewVisible = false
      // 视频暂停播放
      if (this.$refs.previewVideo) {
        this.$refs.previewVideo.pause()
      }
    },
    valueChange(newValue) {
      if (!newValue) {
        this.fileList = []
        return
      }
      if (typeof newValue === 'string') {
        const imgUrls = newValue.split(',')
        // 判断 url 是否存在, 不存在进行添加
        imgUrls.forEach(imgUrl => {
          for (let i = 0; i < this.fileList.length; i++) {
            const item = this.fileList[i]
            if (item.url === imgUrl) {
              return
            }
          }
          this.fileList.push(this.createAntFileObj(imgUrl))
        })
      } else {
        this.fileList = newValue.map(item => (this.createAntFileObj(item)))
      }
    },
    // 创建 ant fileList 的对象
    createAntFileObj(ossUrl) {
      return {
        status: 'done',
        url: ossUrl,
        uid: this.getUidRandom(),
        name: ossUrl,
        thumbUrl: this.getThumbUrl(ossUrl)
      }
    },
    // 预览图片
    getThumbUrl(ossUrl) {
      switch (this.type) {
        // 视频, 使用 oss 封面图功能, 文档: https://help.aliyun.com/zh/oss/user-guide/video-snapshots
        case 'video':
          return ossUrl + '?x-oss-process=video/snapshot,t_0,f_jpg,w_0,h_0,m_fast'
        // 图片/默认
        case 'img':
        default:
          return ossUrl
      }
    },
    // 向外同步值
    emitChange() {
      this.$emit('input', this.isArrayData ? this.fileList.map(item => item.url) : this.fileList.map(item => item.url).join(','))
    },
    // 开始拖拽
    handleDragStart(index, event) {
      this.draggingIndex = index
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        try {
          event.dataTransfer.setData('text/plain', String(index))
        } catch (e) {
          // 部分浏览器限制 setData，忽略即可
        }
      }
    },
    // 拖拽经过
    handleDragOver(index, event) {
      if (this.draggingIndex === null) {
        return
      }
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move'
      }
      if (this.dragOverIndex !== index) {
        this.dragOverIndex = index
      }
    },
    // 放下，执行排序
    handleDrop(index, event) {
      let from = this.draggingIndex
      if (from === null && event.dataTransfer) {
        const data = event.dataTransfer.getData('text/plain')
        from = data === '' ? null : Number(data)
      }
      this.reorder(from, index)
      this.draggingIndex = null
      this.dragOverIndex = null
    },
    // 拖拽结束
    handleDragEnd() {
      this.draggingIndex = null
      this.dragOverIndex = null
    },
    // 左右箭头移动图片
    moveImage(index, direction) {
      const to = direction === 'left' ? index - 1 : index + 1
      this.reorder(index, to)
    },
    // 移动元素位置
    reorder(from, to) {
      if (from === null || Number.isNaN(from) || from === to) {
        return
      }
      if (from < 0 || from >= this.fileList.length || to < 0 || to >= this.fileList.length) {
        return
      }
      const list = this.fileList.slice()
      const target = list.splice(from, 1)[0]
      list.splice(to, 0, target)
      this.fileList = list
      this.emitChange()
    }
  },
  watch: {
    // 监听上级 value 改变
    value(newValue) {
      this.valueChange(newValue)
    }
  }
}
</script>

<style lang="less" scoped>
.image-list {
  display: flex;
  flex-wrap: wrap; /* 图片超出时换行 */
  gap: 8px;
}
.upload-container {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap; /* 超出换行，避免被遮挡 */
  gap: 8px;
}
.image-item {
  position: relative;
  width: 104px;
  height: 104px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  padding: 4px;
  padding-top: 0;
  cursor: move;
  transition: box-shadow 0.2s, opacity 0.2s;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  // 拖拽提示图标，默认隐藏
  .drag-tip {
    display: none;
    position: absolute;
    top: 1px;
    left: 1px;
    padding: 0 4px;
    line-height: 20px;
    font-size: 14px;
    color: #fff;
    background: rgba(0, 0, 0, 0.45);
    border-radius: 2px;
    pointer-events: none;
  }

  // 悬浮时显示拖拽提示
  &:hover .drag-tip {
    display: block;
  }

  // 正在拖拽的元素
  &.is-dragging {
    opacity: 0.4;
  }

  // 拖拽经过的目标位置
  &.is-drag-over {
    box-shadow: -3px 0 0 0 #1890ff;
    border-color: #1890ff;
  }

  // 删除按钮默认隐藏
  .delete-btn {
    display: none;
    position: absolute;
    top: 1px;
    right: 1px;
    color: #ff4d4f;  // 红色
    cursor: pointer;
    background: rgba(255, 255, 255, 0.8);  // 白色半透明背景
    &:hover {
      color: #ff7875;  // 红色悬浮效果
      background: rgba(255, 255, 255, 0.95);
    }
  }

  // 悬浮时显示删除按钮
  &:hover .delete-btn {
    display: block;
  }

  .image-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    background: transparent;  // 透明背景
    padding: 4px;

    .arrow-btn {
      color: #1890ff;  // 蓝色
      padding: 0 4px;
      height: 24px;
      background: rgba(255, 255, 255, 0.8);  // 白色半透明背景
      border-radius: 4px;
      cursor: pointer;

      &[disabled] {
        color: rgba(0, 0, 0, 0.25);  // 禁用状态颜色
        background: rgba(255, 255, 255, 0.6);
        cursor: not-allowed;
      }

      &:hover:not([disabled]) {
        color: #40a9ff;  // 蓝色悬浮效果
        background: rgba(255, 255, 255, 0.95);
      }
    }
  }
}
</style>
